import { v4 as uuidv4 } from 'uuid'
import store from '@/store'
import { authHelpers } from '@/api/authHelpers'
import { isSessionExpiredError } from '@/utils/sessionExpired'

let cypressRunning
try {
  if (Cypress) {
    cypressRunning = true
  }
} catch (e) {
  cypressRunning = false
}

class AppSyncSubscriptionClient {
  constructor () {
    this.ws = null
    this.endpoint = null
    this.region = null
    this.auth = null
    this.subscriptions = new Map()
    this.connectionState = 'disconnected' // disconnected, connecting, connected
    this.reconnectAttempts = 0
    this.maxReconnectDelay = 30000 // 30 seconds
    this.keepaliveTimeout = null
    this.keepaliveTimeoutDuration = 60000 // 60 seconds
    this.pendingSubscriptions = []
    this.isClosing = false
    this.reconnectTimer = null
    this.connectionToken = null
    this.rejectedToken = null
  }

  init ({ endpoint, region, auth }) {
    if (cypressRunning) {
      this.endpoint = Cypress.env('VUE_APP_APPSYNC_GRAPHQL_ENDPOINT')
      this.region = Cypress.env('VUE_APP_APPSYNC_REGION')
    } else {
      this.endpoint = endpoint || process.env.VUE_APP_APPSYNC_GRAPHQL_ENDPOINT
      this.region = region || process.env.VUE_APP_APPSYNC_REGION
    }
    this.auth = auth
  }

  async getRealtimeEndpoint (jwtToken) {
    // Convert GraphQL endpoint to WebSocket endpoint
    // https://xxx.appsync-api.region.amazonaws.com/graphql
    // -> wss://xxx.appsync-realtime-api.region.amazonaws.com/graphql
    const url = new URL(this.endpoint)
    const realtimeHost = url.host.replace('appsync-api', 'appsync-realtime-api')

    // AppSync realtime requires connection params in URL with JWT token
    const header = btoa(JSON.stringify({
      host: url.host,
      Authorization: jwtToken
    }))
    const payload = btoa(JSON.stringify({}))

    const realtimeUrl = `wss://${realtimeHost}/graphql?header=${header}&payload=${payload}`
    return realtimeUrl
  }

  getValidToken () {
    return this.auth.jwtToken()
  }

  async connect () {
    if (this.connectionState === 'connected' || this.connectionState === 'connecting') {
      return
    }

    this.connectionState = 'connecting'
    this.isClosing = false

    try {
      const rejectedIdToken = this.rejectedToken
      this.rejectedToken = null
      const jwtToken = await this.auth.jwtToken({ rejectedIdToken })

      if (!jwtToken) {
        console.error('[AppSync] No JWT token available')
        this.connectionState = 'disconnected'
        if (store.state.isUserLoggedIn) {
          authHelpers.expireSession(null, 'realtime')
        }
        return
      }

      this.connectionToken = jwtToken
      const realtimeUrl = await this.getRealtimeEndpoint(jwtToken)
      this.ws = new WebSocket(realtimeUrl, 'graphql-ws')

      this.ws.onopen = async () => {
        // console.log('[AppSync] WebSocket connected, sending connection_init')
        await this.sendConnectionInit()
      }

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data)
      }

      this.ws.onerror = (error) => {
        console.error('[AppSync] WebSocket error:', error)
      }

      this.ws.onclose = (event) => {
        // console.log(`[AppSync] WebSocket closed (code: ${event.code}, reason: ${event.reason || 'none'})`)
        this.connectionState = 'disconnected'
        this.clearKeepaliveTimeout()

        // Attempt reconnection unless explicitly closing
        if (!this.isClosing) {
          this.scheduleReconnect()
        }
      }
    } catch (error) {
      console.error('[AppSync] Connection error:', error)
      this.connectionState = 'disconnected'

      if (store.state.isUserLoggedIn && isSessionExpiredError(error)) {
        authHelpers.expireSession(null, 'realtime')

        return
      }
      this.scheduleReconnect()
    }
  }

  async sendConnectionInit () {
    try {
      // Authorization is already in the WebSocket URL, so connection_init is just an empty payload
      const message = {
        type: 'connection_init'
      }

      this.ws.send(JSON.stringify(message))
    } catch (error) {
      console.error('[AppSync] Error sending connection init:', error)
      this.close()
      this.scheduleReconnect()
    }
  }

  handleMessage (data) {
    try {
      const message = JSON.parse(data)

      switch (message.type) {
        case 'connection_ack':
          // console.log('[AppSync] Connection acknowledged')
          this.connectionState = 'connected'
          this.reconnectAttempts = 0
          this.resetKeepaliveTimeout()

          // Register any pending subscriptions
          this.registerPendingSubscriptions()
          break

        case 'ka':
          // Keepalive message - reset timeout
          this.resetKeepaliveTimeout()
          break

        case 'start_ack':
          // console.log('[AppSync] Subscription started:', message.id)
          break

        case 'data':
          this.handleSubscriptionData(message)
          break

        case 'error':
          this.handleSubscriptionError(message)
          break

        case 'complete':
          // console.log('[AppSync] Subscription complete:', message.id)
          this.subscriptions.delete(message.id)
          break

        case 'connection_error':
          console.error('[AppSync] Connection error:', message)
          if (message.payload?.errors) {
            const errorMessage = JSON.stringify(message.payload.errors)
            if (errorMessage.includes('Unauthorized') || errorMessage.includes('Not Authorized')) {
              this.rejectedToken = this.connectionToken
            }
          }
          // Close the WebSocket - onclose handler will trigger reconnect
          if (this.ws) {
            this.ws.close()
          }
          break

        default:
          // console.log('[AppSync] Unknown message type:', message.type)
      }
    } catch (error) {
      console.error('[AppSync] Error handling message:', error)
    }
  }

  handleSubscriptionData (message) {
    const subscription = this.subscriptions.get(message.id)
    if (subscription && subscription.onNext) {
      try {
        subscription.onNext(message.payload.data)
      } catch (error) {
        console.error('[AppSync] Error in subscription callback:', error)
      }
    }
  }

  handleSubscriptionError (message) {
    const subscription = this.subscriptions.get(message.id)
    if (subscription && subscription.onError) {
      subscription.onError(message.payload)
    }
  }

  resetKeepaliveTimeout () {
    this.clearKeepaliveTimeout()
    this.keepaliveTimeout = setTimeout(() => {
      // console.warn('[AppSync] Keepalive timeout - reconnecting')
      this.reconnect()
    }, this.keepaliveTimeoutDuration)
  }

  clearKeepaliveTimeout () {
    if (this.keepaliveTimeout) {
      clearTimeout(this.keepaliveTimeout)
      this.keepaliveTimeout = null
    }
  }

  scheduleReconnect () {
    // Clear any pending reconnect
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    )

    // console.log(`[AppSync] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`)

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.reconnectAttempts++
      this.reconnect()
    }, delay)
  }

  reconnect () {
    // Save current subscriptions to re-register after reconnect
    const activeSubscriptions = Array.from(this.subscriptions.values())

    // Detach event handlers from old socket BEFORE closing
    // This prevents the old socket's onclose from firing and triggering another reconnect
    if (this.ws) {
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.onopen = null
    }

    // Close existing connection
    this.close(false) // Don't clear subscriptions

    // Clear and re-add to pending
    this.pendingSubscriptions = activeSubscriptions

    // Reconnect
    this.connect()
  }

  registerPendingSubscriptions () {
    for (const subscription of this.pendingSubscriptions) {
      this.startSubscription(subscription)
    }
    this.pendingSubscriptions = []
  }

  subscribe ({ query, variables = {}, onNext, onError }) {
    const subscription = {
      id: uuidv4(),
      query,
      variables,
      onNext,
      onError: onError || ((error) => console.error('[AppSync] Subscription error:', error))
    }

    // Add to subscriptions map
    this.subscriptions.set(subscription.id, subscription)

    // If connected, start immediately. Otherwise, queue for after connection
    if (this.connectionState === 'connected') {
      this.startSubscription(subscription)
    } else {
      this.pendingSubscriptions.push(subscription)
      // Ensure we're connecting
      if (this.connectionState === 'disconnected') {
        this.connect()
      }
    }

    // Return unsubscribe function
    return {
      unsubscribe: () => {
        this.unsubscribe(subscription.id)
      }
    }
  }

  async startSubscription (subscription) {
    try {
      const jwtToken = await this.getValidToken()

      if (!jwtToken) {
        console.error('[AppSync] No JWT token available for subscription')
        if (subscription.onError) {
          subscription.onError(new Error('No JWT token available'))
        }
        return
      }

      const message = {
        id: subscription.id,
        type: 'start',
        payload: {
          data: JSON.stringify({
            query: subscription.query,
            variables: subscription.variables
          }),
          extensions: {
            authorization: {
              host: new URL(this.endpoint).host,
              Authorization: jwtToken
            }
          }
        }
      }

      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(message))
      }
    } catch (error) {
      console.error('[AppSync] Error starting subscription:', error)
      if (subscription.onError) {
        subscription.onError(error)
      }
    }
  }

  unsubscribe (subscriptionId) {
    const subscription = this.subscriptions.get(subscriptionId)
    if (!subscription) {
      return
    }

    // Send stop message if connected
    if (this.connectionState === 'connected' && this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        const message = {
          id: subscriptionId,
          type: 'stop'
        }
        this.ws.send(JSON.stringify(message))
      } catch (error) {
        console.error('[AppSync] Error unsubscribing:', error)
      }
    }

    // Remove from subscriptions map
    this.subscriptions.delete(subscriptionId)

    // Remove from pending if present
    this.pendingSubscriptions = this.pendingSubscriptions.filter(
      (sub) => sub.id !== subscriptionId
    )
  }

  close (clearSubscriptions = true) {
    this.isClosing = true
    this.connectionState = 'disconnected'
    this.clearKeepaliveTimeout()

    // Clear any pending reconnect
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.ws) {
      // Detach event handlers before closing to prevent them from firing
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.onopen = null

      try {
        this.ws.close()
      } catch (error) {
        console.error('[AppSync] Error closing WebSocket:', error)
      }
      this.ws = null
    }

    if (clearSubscriptions) {
      this.subscriptions.clear()
      this.pendingSubscriptions = []
    }

    this.connectionToken = null
  }
}

// Export singleton instance
const subscriptionClient = new AppSyncSubscriptionClient()
export default subscriptionClient
